import React, { useEffect, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useToast } from '../components/ToastProvider';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters';

interface Deal {
  id: string;
  companyName: string;
  status: 'OPEN' | 'QUALIFIED' | 'OUTREACH_STARTED' | 'MEETING_SCHEDULED' | 'PROPOSAL_SENT' | 'IN_NEGOTIATION' | 'CLOSED_WON' | 'CLOSED_LOST';
  revenueAmount?: number;
  commissionAmount?: number;
  setterCommissionAmount?: number;
  closerCommissionAmount?: number;
  closerCommissionTier?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  closedAt?: string;
}

interface DealDetail {
  id: string;
  companyName: string;
  companyStatus: string;
  status: 'OPEN' | 'QUALIFIED' | 'OUTREACH_STARTED' | 'MEETING_SCHEDULED' | 'PROPOSAL_SENT' | 'IN_NEGOTIATION' | 'CLOSED_WON' | 'CLOSED_LOST';
  revenueAmount?: number;
  commissionAmount?: number;
  setterCommissionAmount?: number;
  closerCommissionAmount?: number;
  closerCommissionTier?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  closedAt?: string;
  setterEmail?: string;
  closerEmail?: string;
  leadStatus?: string;
  partnerEmail?: string;
  partnerPhoneNumber?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  salesTip?: string;
  closerComment?: string;
  createdAt: string;
}

const PartnerDeals: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const toast = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [detailDeal, setDetailDeal] = useState<DealDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const canViewAdminDetails = user?.role === 'ADMIN' || user?.role === 'CLOSER';

  const fetchDeals = async () => {
    const response = await axios.get('/api/partner/deals');
    setDeals(response.data);
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  const openDealDetail = async (dealId: string) => {
    if (!canViewAdminDetails) return;
    try {
      setLoadingDetail(true);
      const res = await axios.get(`/api/admin/deals/${dealId}`);
      setDetailDeal(res.data);
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('deals.loadError')), 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <section>
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('deals.partnerTitle')}</h1>
      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('deals.partnerTitle')}</div>
            <div className="text-xs text-gray-600">{t('deals.partnerSummarySubtitle', t('deals.partnerSubtitle'))}</div>
          </div>
          <button onClick={fetchDeals} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">{t('common.refresh')}</button>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {deals.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">{t('deals.partnerEmpty')}</div>
          ) : (
            deals.map((deal) => (
              <MobileDataCard
                key={deal.id}
                title={deal.companyName}
                badge={<StatusBadge kind="deal" status={deal.status} labelKeyPrefix="partnerStatus.deal" />}
                rows={[
                  { label: t('deals.closedAt'), value: deal.closedAt ? formatDate(deal.closedAt, i18n.resolvedLanguage) : '-' },
                ]}
                actions={canViewAdminDetails ? (
                  <button
                    onClick={() => openDealDetail(deal.id)}
                    disabled={loadingDetail}
                    className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-semibold disabled:opacity-60"
                  >
                    {t('deals.viewDetails')}
                  </button>
                ) : undefined}
              />
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-left table-auto text-[15px] md:text-base">
          <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
            <tr>
              <th className="px-5 py-3 font-semibold">{t('deals.company')}</th>
              <th className="px-5 py-3 font-semibold">{t('deals.status')}</th>
              <th className="px-5 py-3 font-semibold">{t('deals.closedAt')}</th>
              {canViewAdminDetails && <th className="px-5 py-3 font-semibold">{t('deals.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {deals.length === 0 ? (
              <tr>
                <td colSpan={canViewAdminDetails ? 4 : 3} className="px-5 py-10 text-center text-base text-gray-500">{t('deals.partnerEmpty')}</td>
              </tr>
            ) : (
              deals.map((deal, idx) => (
                <tr key={deal.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                  <td className="px-5 py-4 font-medium text-[#111D4E]">{deal.companyName}</td>
                  <td className="px-5 py-4">
                    <StatusBadge kind="deal" status={deal.status} labelKeyPrefix="partnerStatus.deal" />
                  </td>
                  <td className="px-5 py-4">{deal.closedAt ? formatDate(deal.closedAt, i18n.resolvedLanguage) : '-'}</td>
                  {canViewAdminDetails && (
                    <td className="px-5 py-4">
                      <button
                        onClick={() => openDealDetail(deal.id)}
                        disabled={loadingDetail}
                        className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-sm disabled:opacity-60"
                      >
                        {t('deals.viewDetails')}
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {detailDeal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">{t('deals.detailsTitle')}</div>
              <div className="text-xs text-gray-600">{t('deals.partnerDetailsSubtitle')}</div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="font-semibold">{t('deals.company')}:</span> {detailDeal.companyName}</div>
              <div><span className="font-semibold">{t('deals.companyStatus')}:</span> <StatusBadge kind="company" status={detailDeal.companyStatus} /></div>
              <div><span className="font-semibold">{t('deals.status')}:</span> <StatusBadge kind="deal" status={detailDeal.status} /></div>
              <div><span className="font-semibold">{t('deals.createdAt')}:</span> {formatDateTime(detailDeal.createdAt, i18n.resolvedLanguage)}</div>
              <div><span className="font-semibold">{t('deals.setter')}:</span> {detailDeal.setterEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.closer')}:</span> {detailDeal.closerEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.closerTier')}:</span> {detailDeal.closerCommissionTier ? t(`closerType.${detailDeal.closerCommissionTier}`) : '-'}</div>
              <div><span className="font-semibold">{t('deals.revenue')}:</span> {detailDeal.revenueAmount == null ? '-' : formatCurrency(detailDeal.revenueAmount, i18n.resolvedLanguage)}</div>
              <div><span className="font-semibold">{t('deals.commission')}:</span> {detailDeal.commissionAmount == null ? '-' : formatCurrency(detailDeal.commissionAmount, i18n.resolvedLanguage)}</div>
              <div><span className="font-semibold">{t('deals.setterCommission')}:</span> {detailDeal.setterCommissionAmount == null ? '-' : formatCurrency(detailDeal.setterCommissionAmount, i18n.resolvedLanguage)}</div>
              <div><span className="font-semibold">{t('deals.closerCommission')}:</span> {detailDeal.closerCommissionAmount == null ? '-' : formatCurrency(detailDeal.closerCommissionAmount, i18n.resolvedLanguage)}</div>
              <div><span className="font-semibold">{t('deals.closedAt')}:</span> {detailDeal.closedAt ? formatDateTime(detailDeal.closedAt, i18n.resolvedLanguage) : '-'}</div>
              <div><span className="font-semibold">{t('deals.leadStatus')}:</span> {detailDeal.leadStatus ? <StatusBadge kind="lead" status={detailDeal.leadStatus} /> : '-'}</div>
              <div><span className="font-semibold">{t('deals.partner')}:</span> {detailDeal.partnerEmail || '-'}</div>
              <div><span className="font-semibold">{t('deals.setterPhone')}:</span> {detailDeal.partnerPhoneNumber || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactName')}:</span> {detailDeal.contactName || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactEmail')}:</span> {detailDeal.contactEmail || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactPhone')}:</span> {detailDeal.contactPhone || '-'}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('submitLead.salesTip')}:</span> {detailDeal.salesTip || '-'}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('admin.leads.closerComment')}:</span> {detailDeal.closerComment || '-'}</div>
            </div>
            <div className="px-6 pb-6 flex justify-end">
              <button
                type="button"
                onClick={() => setDetailDeal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PartnerDeals;
